import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Returns how far (in CSS px) the on-screen keyboard overlaps the *currently
 * visible* viewport — i.e. how much bottom padding an input needs to clear it.
 * Returns 0 when the keyboard is closed.
 *
 * The correct measurement depends on whether the native WebView resizes itself
 * when the keyboard opens, which differs by platform — so we branch:
 *
 *  - iOS (native): the WebView honors `Keyboard.resize: 'none'` and does NOT
 *    shrink — the keyboard overlays our content. visualViewport math is
 *    unreliable here because iOS *pans* the page to reveal the focused field,
 *    which bumps `visualViewport.offsetTop` and cancels out the overlap, so the
 *    input ends up back under the keyboard. The Capacitor Keyboard plugin
 *    reports the exact keyboard height via `keyboardWillShow`, which is what we
 *    use to lift the input. (This is the path that worked before it was removed
 *    to fix Android — the fix was to branch, not to drop it for everyone.)
 *
 *  - Android (native) + web: on Android with targetSdk >= 35 (edge-to-edge,
 *    enforced on Android 15+) the system ignores `windowSoftInputMode` and
 *    Capacitor resizes the WebView above the keyboard regardless of
 *    `resize: 'none'`. The layout viewport (and `100dvh`) already shrink, so
 *    `innerHeight - visualViewport.height` is ~0 and NO extra padding is needed
 *    — using the plugin's absolute keyboardHeight here would double-count and
 *    shove the input toward the top of the screen. visualViewport measures the
 *    actual uncovered area, which is correct on Android and mobile web alike.
 */
export function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    // iOS: trust the native keyboard height from the plugin.
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      let cancelled = false;
      const showHandle = Keyboard.addListener('keyboardWillShow', (info) => {
        if (!cancelled) setHeight(info.keyboardHeight);
      });
      const hideHandle = Keyboard.addListener('keyboardWillHide', () => {
        if (!cancelled) setHeight(0);
      });
      return () => {
        cancelled = true;
        showHandle.then((h) => h.remove()).catch(() => {});
        hideHandle.then((h) => h.remove()).catch(() => {});
      };
    }

    // Android + web: measure the uncovered area from visualViewport.
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // Bottom gap between the layout viewport and the visible (visual)
      // viewport. offsetTop covers the case where the page is panned/scrolled
      // up to reveal a focused field rather than resized.
      const overlap = Math.max(
        0,
        Math.round(window.innerHeight - vv.height - vv.offsetTop),
      );
      setHeight(overlap);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return height;
}
